import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
  Col,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import ExpenseTxnServices from "../../../Services/QRCodeServices/ExpenseTxnServices/ExpenseTxnServices";
import ExpenseHeadingServices from "../../../Services/QRCodeServices/ExpenseHeadingServices/ExpenseHeadingServices";

const ExpenseTxnAddEditModal = ({
  isOpen,
  toggle,
  isEdit,
  selectedTxn,
  onSuccess,
}) => {
  const [expenseHeadings, setExpenseHeadings] = useState([]);

  const validationSchema = Yup.object({
    // headerId: Yup.number().required("Please select a heading"),
    title: Yup.string().required("Title is required"),
    transacAmount: Yup.number()
      .min(0, "Amount must be greater than or equal to 0")
      .required("Transaction amount is required"),
    description: Yup.string(),
    remarks: Yup.string(),
  });

  // Formik setup
  const formik = useFormik({
    initialValues: {
      headerId: selectedTxn?.headerId || "",
      title: selectedTxn?.title || "",
      valueDate: selectedTxn?.valueDate || "",
      transacAmount: selectedTxn?.transacAmount || "",
      valueDate: selectedTxn?.valueDate || "",
      description: selectedTxn?.description || "",
      remarks: selectedTxn?.remarks || "",
    },
    validationSchema: validationSchema,
    enableReinitialize: true,

    onSubmit: async (values) => {
      try {
        const payload = {
          id: isEdit ? selectedTxn.id : undefined,
          headerId: Number(values.headerId),
          title: values.title,
          transacAmount: Number(values.transacAmount),
          valueDate: values.valueDate,
          description: values.description,
          remarks: values.remarks,
        };

        if (isEdit) {
          await ExpenseTxnServices.update(selectedTxn.id, payload);
          toast.success("Expense Transaction updated successfully!");
        } else {
          await ExpenseTxnServices.create(payload);
          toast.success("Expense Transaction created successfully!");
        }
        onSuccess();
        formik.resetForm();
      } catch (error) {
        toast.error(
          error.response?.data || "Failed to save expense transaction"
        );
        console.error("Error saving expense transaction:", error);
      }
    },
  });

  const handleCancel = () => {
    formik.resetForm();
    toggle();
  };

  async function fetchExpenseHeadings() {
    try {
      const fetchedHeadings = await ExpenseHeadingServices.getList();
      setExpenseHeadings(fetchedHeadings);
    } catch (err) {
      console.error("Error fetching expense headings:", err);
    }
  }

  useEffect(() => {
    fetchExpenseHeadings();
  }, []);

  return (
    <Modal isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={handleCancel}>
        {isEdit ? "Edit Expense Transaction" : "Create Expense Transaction"}
      </ModalHeader>
      <Form onSubmit={formik.handleSubmit}>
        <ModalBody>
          <Row>
            <Col md={12} className="mb-3">
              <Label for="valueDate">Value Date</Label>
              <Input
                type="date"
                id="valueDate"
                name="valueDate"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.valueDate}
                invalid={formik.touched.valueDate && formik.errors.valueDate}
              />
              {formik.touched.valueDate && formik.errors.valueDate && (
                <FormFeedback>{formik.errors.valueDate}</FormFeedback>
              )}
            </Col>
          </Row>
          <Row>
            <Col md={12} className="mb-3">
              <Label for="headerId">Expense Heading</Label>
              <Input
                type="select"
                id="headerId"
                name="headerId"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.headerId}
                invalid={formik.touched.headerId && formik.errors.headerId}
              >
                <option value="">Select a heading</option>
                {expenseHeadings.map((heading) => (
                  <option key={heading.id} value={heading.id}>
                    {heading.title}
                  </option>
                ))}
              </Input>
              {formik.touched.headerId && formik.errors.headerId && (
                <FormFeedback>{formik.errors.headerId}</FormFeedback>
              )}
            </Col>
          </Row>
          <Row>
            <Col md={12} className="mb-3">
              <Label for="title">Title</Label>
              <Input
                type="text"
                id="title"
                name="title"
                placeholder="Enter Title"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.title}
                invalid={formik.touched.title && formik.errors.title}
              />
              {formik.touched.title && formik.errors.title && (
                <FormFeedback>{formik.errors.title}</FormFeedback>
              )}
            </Col>
          </Row>
          <Row>
            <Col md={12} className="mb-3">
              <Label for="transacAmount">Transaction Amount</Label>
              <Input
                type="number"
                id="transacAmount"
                name="transacAmount"
                placeholder="Enter Amount"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.transacAmount}
                invalid={
                  formik.touched.transacAmount && formik.errors.transacAmount
                }
              />
              {formik.touched.transacAmount && formik.errors.transacAmount && (
                <FormFeedback>{formik.errors.transacAmount}</FormFeedback>
              )}
            </Col>
          </Row>
          <Row>
            <Col md={12} className="mb-3">
              <Label for="description">Description</Label>
              <Input
                type="textarea"
                id="description"
                name="description"
                placeholder="Enter Description"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.description}
                invalid={
                  formik.touched.description && formik.errors.description
                }
              />
              {formik.touched.description && formik.errors.description && (
                <FormFeedback>{formik.errors.description}</FormFeedback>
              )}
            </Col>
          </Row>
          <Row>
            <Col md={12} className="mb-3">
              <Label for="remarks">Remarks</Label>
              <Input
                type="textarea"
                id="remarks"
                name="remarks"
                placeholder="Enter Remarks"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.remarks}
                invalid={formik.touched.remarks && formik.errors.remarks}
              />
              {formik.touched.remarks && formik.errors.remarks && (
                <FormFeedback>{formik.errors.remarks}</FormFeedback>
              )}
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button type="submit" color="success" disabled={formik.isSubmitting}>
            {isEdit ? "Update" : "Save"}
          </Button>
          <Button color="danger" onClick={handleCancel} type="button">
            Cancel
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default ExpenseTxnAddEditModal;
